/**
 * Per-channel message counter over a rolling five-minute window, bucketed
 * per second in a ring buffer. Recording a message is O(1) and allocates
 * nothing; reading the windows is O(300) however busy the chat is.
 *
 * Replaces keeping one timestamp per message, which for a 50 msg/s chat meant
 * filtering a 15,000-entry array on every tick for every monitored channel.
 */
export const WINDOW_S = 300;
export const SHORT_S = 10;

export interface WindowMetrics {
  /** Messages in the last 10 seconds */
  count10s: number;
  /** Messages in the last 5 minutes */
  count5m: number;
  /** Messages ever counted on this channel */
  messageCount: number;
  /** Seconds of observation backing the long window, capped at WINDOW_S */
  elapsedSeconds: number;
}

export class RateWindow {
  private counts = new Int32Array(WINDOW_S);
  /** Absolute second each slot currently holds; -1 = never written */
  private seconds = new Float64Array(WINDOW_S).fill(-1);
  messageCount = 0;
  startedAt: number;

  constructor(startedAt: number) {
    this.startedAt = startedAt;
  }

  record(now: number): void {
    const sec = Math.floor(now / 1000);
    const slot = sec % WINDOW_S;
    if (this.seconds[slot] !== sec) {
      this.seconds[slot] = sec;
      this.counts[slot] = 0;
    }
    this.counts[slot]++;
    this.messageCount++;
  }

  /** A channel that never spoke starts its window when it is actually joined */
  restartIfSilent(now: number): void {
    if (this.messageCount === 0) this.startedAt = now;
  }

  metrics(now: number): WindowMetrics {
    const sec = Math.floor(now / 1000);
    let count10s = 0;
    let count5m = 0;
    for (let i = 0; i < WINDOW_S; i++) {
      const s = this.seconds[i];
      if (s < 0) continue;
      const age = sec - s;
      if (age < 0 || age >= WINDOW_S) continue;
      const n = this.counts[i];
      count5m += n;
      if (age < SHORT_S) count10s += n;
    }
    const elapsed = Math.max(0, (now - this.startedAt) / 1000);
    return {
      count10s,
      count5m,
      messageCount: this.messageCount,
      elapsedSeconds: Math.min(elapsed, WINDOW_S),
    };
  }

  /** Messages per bucket over the last `bucketSeconds * buckets` seconds, oldest first */
  history(now: number, bucketSeconds = 5, buckets = 60): number[] {
    const out = new Array<number>(buckets).fill(0);
    const sec = Math.floor(now / 1000);
    const span = bucketSeconds * buckets;
    for (let i = 0; i < WINDOW_S; i++) {
      const s = this.seconds[i];
      if (s < 0) continue;
      const age = sec - s;
      if (age < 0 || age >= span) continue;
      out[buckets - 1 - Math.floor(age / bucketSeconds)] += this.counts[i];
    }
    return out;
  }
}
